
import React from 'react';
import { useEnhancedSubscription } from '@/hooks/useEnhancedSubscription';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { UpgradeGate } from '@/components/ui/UpgradeGate';
import { FeatureType } from '@/hooks/useAdvancedFeatureGating';
import { Loader2 } from 'lucide-react';

interface SubscriptionProtectedRouteProps {
  children: React.ReactNode;
  requiredTier: 'freemium' | 'pro' | 'investor' | 'agent_pro' | 'enterprise';
  featureName: string;
  featureDescription: string;
}

const TIER_HIERARCHY: Record<string, number> = {
  freemium: 0,
  pro: 1,
  investor: 2,
  agent_pro: 3,
  enterprise: 4
};

// Map subscription tiers to FeatureType for UpgradeGate component
const TIER_TO_FEATURE_MAP: Record<string, FeatureType> = {
  freemium: 'basic_calculator',
  pro: 'advanced_calculator',
  investor: 'investment_analysis',
  agent_pro: 'branded_reports',
  enterprise: 'admin_dashboard'
};

export const SubscriptionProtectedRoute: React.FC<SubscriptionProtectedRouteProps> = ({
  children,
  requiredTier,
  featureName,
  featureDescription
}) => {
  const { subscription, loading } = useEnhancedSubscription();
  const { hasRole, isLoading: authLoading } = useAuth();

  // Show loading state
  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Admins/Owners bypass all restrictions
  if (hasRole(['admin', 'owner'])) {
    return <>{children}</>;
  }

  // Check if user has required tier
  const currentTier = subscription?.tier || 'freemium';
  const currentTierLevel = TIER_HIERARCHY[currentTier] || 0;
  const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 0;
  const hasAccess = currentTierLevel >= requiredTierLevel;

  // If user doesn't have access, show upgrade gate
  if (!hasAccess) {
    const featureType = TIER_TO_FEATURE_MAP[requiredTier] || 'advanced_calculator';
    return (
      <UpgradeGate
        feature={featureType as FeatureType}
        title={featureName}
        description={featureDescription}
      />
    );
  }

  return <>{children}</>;
};
